<template>
  <!-- 메인 모달 -->
  <AModal
    :model-value="show"
    size="xl"
    body-flush
    @update:model-value="handleModalUpdate"
    @close="closeModal"
  >
    <template #title>
      <span class="exf__title">
        <i class="material-icons-round exf__title-icon">folder_special</i>
        문제은행 파일 관리
      </span>
    </template>
    <template #head>
      <APill kind="yellow">이미지 &amp; 오디오</APill>
    </template>

    <div class="exf__body">
      <!-- 탭 -->
      <div class="p-tabs exf__tabs">
        <button
          type="button"
          :class="['p-tabs__tab', { 'is-active': activeTab === 'images' }]"
          @click="activeTab = 'images'"
        >
          <i class="material-icons-round exf__tab-icon">image</i>
          이미지 파일
          <span v-if="images.length > 0" class="p-tabs__count">{{ images.length }}</span>
        </button>
        <button
          type="button"
          :class="['p-tabs__tab', { 'is-active': activeTab === 'audio' }]"
          @click="activeTab = 'audio'"
        >
          <i class="material-icons-round exf__tab-icon">audiotrack</i>
          오디오 파일
          <span v-if="audioFiles.length > 0" class="p-tabs__count">{{ audioFiles.length }}</span>
        </button>
      </div>

      <!-- ── 이미지 탭 ── -->
      <div v-show="activeTab === 'images'" class="exf__panel">
        <!-- 업로드 -->
        <section class="exf__section">
          <header class="exf__section-head">
            <i class="material-icons-round exf__section-icon">cloud_upload</i>
            <span class="exf__section-title">이미지 업로드 (JPG · PNG · GIF · WebP)</span>
          </header>

          <div class="exf__upload-grid">
            <div class="p-field exf__upload-file">
              <label class="p-field__label">파일 선택</label>
              <input
                ref="fileInput"
                type="file"
                multiple
                accept="image/*"
                class="p-input p-input--file"
                @change="onFileSelected"
              />
            </div>
            <div class="exf__upload-action">
              <ABtn
                kind="primary"
                icon="upload"
                block
                :disabled="!hasSelectedFiles || uploadProgress > 0"
                :loading="uploadProgress > 0 && uploadProgress < 100"
                @click="uploadImagesWithMetadata"
              >업로드</ABtn>
            </div>
            <div class="p-field exf__upload-meta">
              <label class="p-field__label">태그 (쉼표로 구분)</label>
              <input
                v-model="imageMetadata.tags"
                type="text"
                class="p-input"
                placeholder="예: 문제, 도표, 수학"
              />
            </div>
          </div>

          <div v-if="uploadProgress > 0 && uploadProgress < 100" class="exf__progress">
            <div class="exf__progress-bar">
              <div class="exf__progress-fill" :style="{ width: uploadProgress + '%' }"></div>
            </div>
            <span class="exf__progress-text">이미지 업로드 중… {{ uploadProgress }}%</span>
          </div>
        </section>

        <!-- 검색 -->
        <section class="exf__section">
          <header class="exf__section-head">
            <i class="material-icons-round exf__section-icon">search</i>
            <span class="exf__section-title">태그로 검색</span>
          </header>
          <div class="p-input-group">
            <input
              v-model="imageSearchQuery"
              type="text"
              class="p-input"
              placeholder="예: 문제, 도표, 수학"
              @keyup.enter="searchImages"
            />
            <ABtn kind="primary" icon="search" :loading="loading" @click="searchImages">
              검색
            </ABtn>
          </div>
        </section>

        <!-- 이미지 목록 -->
        <section class="exf__section">
          <header class="exf__section-head">
            <i class="material-icons-round exf__section-icon">photo_library</i>
            <span class="exf__section-title">이미지 목록</span>
            <APill v-if="images.length > 0" kind="yellow" class="exf__section-extra">
              {{ images.length }}개
            </APill>
          </header>

          <div v-if="loading" class="exf__loading">
            <div class="p-spinner p-spinner--lg"></div>
            <p>이미지를 불러오는 중...</p>
          </div>

          <AEmpty
            v-else-if="images.length === 0"
            title="업로드된 이미지가 없어요"
            sub="위의 업로드 영역에서 이미지를 추가해보세요."
          />

          <div v-else class="exf__images">
            <div v-for="(image, index) in images" :key="index" class="exf__image-card">
              <div class="exf__image-thumb">
                <img :src="image.url" :alt="image.filename" @error="handleImageError" />
                <div class="exf__image-overlay">
                  <button class="p-icon-btn p-icon-btn--on-dark" type="button" v-tooltip title="URL 복사" @click="copyImageUrl(image.filename)">
                    <i class="material-icons-round">content_copy</i>
                  </button>
                  <button class="p-icon-btn p-icon-btn--on-dark" type="button" v-tooltip title="태그 수정" @click="openEditTags(image)">
                    <i class="material-icons-round">edit</i>
                  </button>
                  <button class="p-icon-btn p-icon-btn--on-dark" type="button" v-tooltip title="삭제" @click="deleteImage(image.filename)">
                    <i class="material-icons-round">delete</i>
                  </button>
                </div>
              </div>
              <div class="exf__image-meta">
                <p class="exf__image-name" :title="image.filename">{{ image.filename }}</p>
                <small v-if="image.file_size" class="exf__image-size">{{ formatFileSize(image.file_size) }}</small>
                <div v-if="image.tags && image.tags.length > 0" class="exf__image-tags">
                  <APill v-for="tag in image.tags" :key="tag" kind="info">{{ tag }}</APill>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- ── 오디오 탭 ── -->
      <div v-show="activeTab === 'audio'" class="exf__panel">
        <section class="exf__section">
          <header class="exf__section-head">
            <i class="material-icons-round exf__section-icon">cloud_upload</i>
            <span class="exf__section-title">오디오 업로드 (MP3 · WAV · OGG · M4A · AAC · FLAC)</span>
          </header>

          <div class="exf__upload-grid">
            <div class="p-field exf__upload-file">
              <label class="p-field__label">파일 선택</label>
              <input
                ref="audioFileInput"
                type="file"
                multiple
                accept="audio/*"
                class="p-input p-input--file"
                @change="onAudioFileSelected"
              />
            </div>
            <div class="exf__upload-action">
              <ABtn
                kind="yellow"
                icon="upload"
                block
                :disabled="!hasSelectedAudioFiles || audioUploadProgress > 0"
                :loading="audioUploadProgress > 0 && audioUploadProgress < 100"
                @click="uploadAudioWithMetadata"
              >업로드</ABtn>
            </div>
            <div class="p-field exf__upload-meta">
              <label class="p-field__label">스크립트 (오디오 내용 요약/대본)</label>
              <textarea
                v-model="audioMetadata.script"
                class="p-input p-input--area"
                rows="2"
                placeholder="오디오 내용 요약이나 대본을 입력하세요"
              ></textarea>
            </div>
          </div>

          <div v-if="audioUploadProgress > 0 && audioUploadProgress < 100" class="exf__progress">
            <div class="exf__progress-bar">
              <div class="exf__progress-fill exf__progress-fill--warn" :style="{ width: audioUploadProgress + '%' }"></div>
            </div>
            <span class="exf__progress-text">오디오 업로드 중… {{ audioUploadProgress }}%</span>
          </div>
        </section>

        <section class="exf__section">
          <header class="exf__section-head">
            <i class="material-icons-round exf__section-icon">search</i>
            <span class="exf__section-title">스크립트로 검색</span>
          </header>
          <div class="p-input-group">
            <input
              v-model="audioSearchQuery"
              type="text"
              class="p-input"
              placeholder="예: 영어 듣기, 수학 문제 해설"
              @keyup.enter="searchAudioFiles"
            />
            <ABtn kind="yellow" icon="search" :loading="audioLoading" @click="searchAudioFiles">
              검색
            </ABtn>
          </div>
        </section>

        <section class="exf__section">
          <header class="exf__section-head">
            <i class="material-icons-round exf__section-icon">library_music</i>
            <span class="exf__section-title">오디오 목록</span>
            <APill v-if="audioFiles.length > 0" kind="warn" class="exf__section-extra">
              {{ audioFiles.length }}개
            </APill>
          </header>

          <div v-if="audioLoading" class="exf__loading">
            <div class="p-spinner p-spinner--lg"></div>
            <p>오디오를 불러오는 중...</p>
          </div>

          <AEmpty
            v-else-if="audioFiles.length === 0"
            title="업로드된 오디오가 없어요"
            sub="위의 업로드 영역에서 오디오를 추가해보세요."
          />

          <div v-else class="exf__audios">
            <div v-for="(audio, index) in audioFiles" :key="index" class="exf__audio-card">
              <div class="exf__audio-icon">
                <i class="material-icons-round">audiotrack</i>
              </div>
              <div class="exf__audio-content">
                <h6 class="exf__audio-name" :title="audio.filename">{{ audio.filename }}</h6>
                <small v-if="audio.file_size" class="exf__audio-size">{{ formatFileSize(audio.file_size) }}</small>
                <div v-if="audio.script" class="exf__audio-script">
                  <i class="material-icons-round exf__audio-script-icon">description</i>
                  <span>{{ audio.script.length > 60 ? audio.script.substring(0, 60) + '...' : audio.script }}</span>
                </div>
                <div class="exf__audio-actions">
                  <ABtn
                    size="sm"
                    :icon="currentPlayingAudio === audio.filename ? 'stop' : 'play_arrow'"
                    @click="playAudio(audio.filename)"
                  >{{ currentPlayingAudio === audio.filename ? '정지' : '재생' }}</ABtn>
                  <button class="p-icon-btn" type="button" v-tooltip title="스크립트 수정" @click="openEditScript(audio)">
                    <i class="material-icons-round">edit</i>
                  </button>
                  <button class="p-icon-btn" type="button" v-tooltip title="파일명 복사" @click="copyAudioUrl(audio.filename)">
                    <i class="material-icons-round">content_copy</i>
                  </button>
                  <button class="p-icon-btn p-icon-btn--danger" type="button" v-tooltip title="삭제" @click="deleteAudio(audio.filename)">
                    <i class="material-icons-round">delete</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <template #foot>
      <ABtn icon="close" @click="closeModal">닫기</ABtn>
    </template>
  </AModal>

  <!-- 태그 수정 모달 -->
  <AModal v-model="showEditTagsModal" size="md" :title="`태그 수정${editTagsTarget ? ' — ' + editTagsTarget.filename : ''}`">
    <div class="p-field">
      <label class="p-field__label">태그 (쉼표로 구분)</label>
      <input v-model="editTagsInput" type="text" class="p-input" placeholder="예: 문제, 도표, 수학" />
    </div>
    <template #foot>
      <ABtn @click="showEditTagsModal = false">취소</ABtn>
      <ABtn kind="primary" @click="saveEditTags">저장</ABtn>
    </template>
  </AModal>

  <!-- 스크립트 수정 모달 -->
  <AModal v-model="showEditScriptModal" size="md" :title="`스크립트 수정${editScriptTarget ? ' — ' + editScriptTarget.filename : ''}`">
    <div class="p-field">
      <label class="p-field__label">스크립트</label>
      <textarea v-model="editScriptInput" class="p-input p-input--area" rows="5" placeholder="스크립트를 입력하세요"></textarea>
    </div>
    <template #foot>
      <ABtn @click="showEditScriptModal = false">취소</ABtn>
      <ABtn kind="yellow" @click="saveEditScript">저장</ABtn>
    </template>
  </AModal>
</template>

<script>
import AModal from '@/components/Pineapple/AModal.vue'
import APill from '@/components/Pineapple/APill.vue'
import ABtn from '@/components/Pineapple/ABtn.vue'
import AEmpty from '@/components/Pineapple/AEmpty.vue'

export default {
  name: 'ExamFilesModal',
  components: { AModal, APill, ABtn, AEmpty },
  props: {
    show: { type: Boolean, default: false },
  },
  emits: ['close'],
  data() {
    return {
      activeTab: 'images',
      images: [],
      audioFiles: [],
      uploadProgress: 0,
      audioUploadProgress: 0,
      loading: false,
      audioLoading: false,
      currentPlayingAudio: null,
      currentAudio: null,
      selectedFiles: null,
      selectedAudioFiles: null,
      imageMetadata: { tags: '' },
      audioMetadata: { script: '' },
      imageSearchQuery: '',
      audioSearchQuery: '',
      showEditTagsModal: false,
      editTagsTarget: null,
      editTagsInput: '',
      showEditScriptModal: false,
      editScriptTarget: null,
      editScriptInput: '',
    };
  },
  computed: {
    hasSelectedFiles() {
      return this.selectedFiles && this.selectedFiles.length > 0 && this.imageMetadata.tags.trim() !== '';
    },
    hasSelectedAudioFiles() {
      return this.selectedAudioFiles && this.selectedAudioFiles.length > 0 && this.audioMetadata.script.trim() !== '';
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchImages();
        this.fetchAudioFiles();
      }
    },
  },
  beforeUnmount() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
  },
  methods: {
    handleModalUpdate(value) {
      if (!value) this.closeModal();
    },
    closeModal() {
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }
      this.currentPlayingAudio = null;
      this.$emit('close');
    },

    async fetchImages() {
      this.loading = true;
      try {
        const params = {};
        if (this.imageSearchQuery.trim()) params.tags = this.imageSearchQuery.trim();

        const res = await this.$axios({
          method: 'get',
          url: `/exam-files/imageStorage`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: false,
          params,
        });

        if (res.status === 200) {
          this.images = res.data.map((img) => ({
            ...img,
            url: img.url || `${this.$CONF.ACHIEVE_API_URL}/files/image/stream/${img.filename}?file_type=exam_files`,
          }));
        }
      } catch (error) {
        console.error('Error fetching images:', error);
        this.$notifyVue({ type: 'error', title: '오류', text: '이미지를 불러오는 중 오류가 발생했습니다.' });
      } finally {
        this.loading = false;
      }
    },

    openEditTags(image) {
      this.editTagsTarget = image;
      const currentTags = Array.isArray(image.tags) ? image.tags.join(', ') : (image.tags || '');
      this.editTagsInput = currentTags;
      this.showEditTagsModal = true;
    },

    async saveEditTags() {
      if (!this.editTagsTarget) return;
      try {
        const formData = new FormData();
        formData.append('tags', this.editTagsInput);
        const res = await this.$axios({
          method: 'put',
          url: `/exam-files/files/metadata/${this.editTagsTarget.filename}/tags`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          data: formData,
        });
        if (res.status === 200) {
          this.$notifyVue({ type: 'success', title: '성공', text: '태그가 업데이트되었습니다.' });
          this.showEditTagsModal = false;
          this.editTagsTarget = null;
          this.editTagsInput = '';
          this.fetchImages();
        }
      } catch (e) {
        console.error('saveEditTags error', e);
        this.$notifyVue({ type: 'error', title: '오류', text: '태그 업데이트 중 오류가 발생했습니다.' });
      }
    },

    openEditScript(audio) {
      this.editScriptTarget = audio;
      this.editScriptInput = audio.script || '';
      this.showEditScriptModal = true;
    },

    async saveEditScript() {
      if (!this.editScriptTarget) return;
      try {
        const formData = new FormData();
        formData.append('script', this.editScriptInput);
        const res = await this.$axios({
          method: 'put',
          url: `/exam-files/files/metadata/${this.editScriptTarget.filename}/script`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          data: formData,
        });
        if (res.status === 200) {
          this.$notifyVue({ type: 'success', title: '성공', text: '스크립트가 업데이트되었습니다.' });
          this.showEditScriptModal = false;
          this.editScriptTarget = null;
          this.editScriptInput = '';
          this.fetchAudioFiles();
        }
      } catch (e) {
        console.error('saveEditScript error', e);
        this.$notifyVue({ type: 'error', title: '오류', text: '스크립트 업데이트 중 오류가 발생했습니다.' });
      }
    },

    async fetchAudioFiles() {
      this.audioLoading = true;
      try {
        const params = {};
        if (this.audioSearchQuery.trim()) params.script = this.audioSearchQuery.trim();

        const res = await this.$axios({
          method: 'get',
          url: `/exam-files/audioStorage`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: false,
          params,
        });

        if (res.status === 200) {
          this.audioFiles = res.data.map((audio) => ({
            ...audio,
            url: audio.url || `${this.$CONF.ACHIEVE_API_URL}/files/audio/stream/${audio.filename}?file_type=exam_files`,
          }));
        }
      } catch (error) {
        console.error('Error fetching audio files:', error);
        this.$notifyVue({ type: 'error', title: '오류', text: '오디오를 불러오는 중 오류가 발생했습니다.' });
      } finally {
        this.audioLoading = false;
      }
    },

    validateFilenames(files, fileType = '파일') {
      const koreanFiles = files.filter((file) => this.$hasKoreanInFilename(file.name));
      if (koreanFiles.length > 0) {
        const fileNames = koreanFiles.map((file) => file.name).join(', ');
        this.$notifyVue({
          title: '파일명 오류',
          text: `${fileType} 파일명에 한글이 포함되어 있습니다.\n영문, 숫자, 특수문자만 사용해주세요.\n\n문제 파일: ${fileNames}`,
          type: 'error',
          duration: 5000,
        });
        return false;
      }
      return true;
    },

    onFileSelected(e) {
      const files = Array.from(e.target.files);
      if (!this.validateFilenames(files, '이미지')) {
        e.target.value = '';
        this.selectedFiles = [];
        return;
      }
      this.selectedFiles = files;
    },

    async uploadImagesWithMetadata() {
      if (!this.selectedFiles || this.selectedFiles.length === 0) {
        this.$notifyVue({ type: 'warning', title: '경고', text: '업로드할 이미지 파일을 선택해주세요.' });
        return;
      }
      const imageFiles = Array.from(this.selectedFiles).filter((file) => file.type.startsWith('image/'));
      if (imageFiles.length === 0) {
        this.$notifyVue({ type: 'warning', title: '경고', text: '이미지 파일만 업로드할 수 있습니다.' });
        return;
      }
      this.uploadProgress = 1;
      try {
        for (const file of imageFiles) {
          await this.uploadSingleImageWithMetadata(file);
        }
        this.$notifyVue({ type: 'success', title: '성공', text: '이미지가 성공적으로 업로드되었습니다.' });
        this.fetchImages();
        this.resetImageForm();
      } catch (error) {
        console.error('Image upload error:', error);
        this.$notifyVue({ type: 'error', title: '오류', text: '이미지 업로드 중 오류가 발생했습니다.' });
      } finally {
        this.uploadProgress = 0;
      }
    },

    async uploadSingleImageWithMetadata(file) {
      const formData = new FormData();
      formData.append('file', file);
      if (this.imageMetadata.tags.trim()) {
        const tags = this.imageMetadata.tags.split(',').map((t) => t.trim()).filter(Boolean);
        formData.append('tags', JSON.stringify(tags));
      }
      const res = await this.$axios({
        method: 'post',
        url: `/exam-files/upload/image`,
        api_url: this.$CONF.ACHIEVE_API_URL,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        },
      });
      return res;
    },

    resetImageForm() {
      this.selectedFiles = null;
      this.imageMetadata.tags = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    async deleteImage(filename) {
      if (!window.confirm('정말로 이 이미지를 삭제하시겠습니까?')) return;
      try {
        const res = await this.$axios({
          method: 'delete',
          url: `/exam-files/image/${filename}`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: true,
        });
        if (res.status === 200 || res.status === 204) {
          this.$notifyVue({ type: 'success', title: '성공', text: '이미지가 삭제되었습니다.' });
          this.fetchImages();
        }
      } catch (error) {
        console.error('Error deleting image:', error);
        this.$notifyVue({ type: 'error', title: '오류', text: '이미지 삭제 중 오류가 발생했습니다.' });
      }
    },

    onAudioFileSelected(e) {
      const files = Array.from(e.target.files);
      if (!this.validateFilenames(files, '오디오')) {
        e.target.value = '';
        this.selectedAudioFiles = [];
        return;
      }
      this.selectedAudioFiles = files;
    },

    async uploadAudioWithMetadata() {
      if (!this.selectedAudioFiles || this.selectedAudioFiles.length === 0) {
        this.$notifyVue({ type: 'warning', title: '경고', text: '업로드할 오디오 파일을 선택해주세요.' });
        return;
      }
      const audioFiles = Array.from(this.selectedAudioFiles).filter((file) => file.type.startsWith('audio/'));
      if (audioFiles.length === 0) {
        this.$notifyVue({ type: 'warning', title: '경고', text: '오디오 파일만 업로드할 수 있습니다.' });
        return;
      }
      this.audioUploadProgress = 1;
      try {
        for (const file of audioFiles) {
          await this.uploadSingleAudioWithMetadata(file);
        }
        this.$notifyVue({ type: 'success', title: '성공', text: '오디오가 성공적으로 업로드되었습니다.' });
        this.fetchAudioFiles();
        this.resetAudioForm();
      } catch (error) {
        console.error('Audio upload error:', error);
        this.$notifyVue({ type: 'error', title: '오류', text: '오디오 업로드 중 오류가 발생했습니다.' });
      } finally {
        this.audioUploadProgress = 0;
      }
    },

    async uploadSingleAudioWithMetadata(file) {
      const formData = new FormData();
      formData.append('file', file);
      if (this.audioMetadata.script.trim()) {
        formData.append('script', this.audioMetadata.script.trim());
      }
      const res = await this.$axios({
        method: 'post',
        url: `/exam-files/upload/audio`,
        api_url: this.$CONF.ACHIEVE_API_URL,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          this.audioUploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        },
      });
      return res;
    },

    resetAudioForm() {
      this.selectedAudioFiles = null;
      this.audioMetadata.script = '';
      if (this.$refs.audioFileInput) this.$refs.audioFileInput.value = '';
    },

    async deleteAudio(filename) {
      if (!window.confirm('정말로 이 오디오를 삭제하시겠습니까?')) return;
      try {
        const res = await this.$axios({
          method: 'delete',
          url: `/exam-files/audio/${filename}`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: true,
        });
        if (res.status === 200 || res.status === 204) {
          this.$notifyVue({ type: 'success', title: '성공', text: '오디오가 삭제되었습니다.' });
          this.fetchAudioFiles();
        }
      } catch (error) {
        console.error('Error deleting audio:', error);
        this.$notifyVue({ type: 'error', title: '오류', text: '오디오 삭제 중 오류가 발생했습니다.' });
      }
    },

    async playAudio(filename) {
      if (this.currentPlayingAudio === filename) {
        if (this.currentAudio) {
          this.currentAudio.pause();
          this.currentAudio = null;
          this.currentPlayingAudio = null;
        }
        return;
      }
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }
      try {
        this.currentPlayingAudio = filename;
        const response = await this.$axios({
          method: 'get',
          url: `/files/audio/stream/${filename}`,
          api_url: this.$CONF.ACHIEVE_API_URL,
          params: { file_type: 'exam_files' },
          responseType: 'blob',
          loadingBar: false,
        });
        if (response.status === 200) {
          const audioBlob = response.data;
          const audioUrl = URL.createObjectURL(audioBlob);
          this.currentAudio = new Audio(audioUrl);
          this.currentAudio.addEventListener('ended', () => {
            URL.revokeObjectURL(audioUrl);
            this.currentPlayingAudio = null;
            this.currentAudio = null;
          });
          this.currentAudio.addEventListener('error', (e) => {
            console.error('Audio playback error:', e);
            URL.revokeObjectURL(audioUrl);
            this.$notifyVue({ type: 'error', title: '오류', text: '오디오 재생 중 오류가 발생했습니다.' });
            this.currentPlayingAudio = null;
            this.currentAudio = null;
          });
          await this.currentAudio.play();
        }
      } catch (error) {
        console.error('Audio load error:', error);
        this.$notifyVue({ type: 'error', title: '오류', text: '오디오 파일을 불러오는 중 오류가 발생했습니다.' });
        this.currentPlayingAudio = null;
        this.currentAudio = null;
      }
    },

    copyAudioUrl(filename) {
      navigator.clipboard
        .writeText(filename)
        .then(() => this.$notifyVue({ type: 'success', title: '성공', text: '파일명이 클립보드에 복사되었습니다.' }))
        .catch((err) => {
          console.error('Failed to copy filename: ', err);
          this.$notifyVue({ type: 'error', title: '오류', text: '파일명 복사에 실패했습니다.' });
        });
    },

    copyImageUrl(url) {
      navigator.clipboard
        .writeText(url)
        .then(() => this.$notifyVue({ type: 'success', title: '성공', text: 'URL이 클립보드에 복사되었습니다.' }))
        .catch((err) => {
          console.error('Failed to copy URL: ', err);
          this.$notifyVue({ type: 'error', title: '오류', text: 'URL 복사에 실패했습니다.' });
        });
    },

    handleImageError(event) {
      event.target.style.display = 'none';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    searchImages() {
      this.fetchImages();
    },
    searchAudioFiles() {
      this.fetchAudioFiles();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.exf {
  // ── 헤더 ──
  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: -0.015em;
    color: $p-ink;
  }
  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  // ── 본문 ──
  &__body {
    background: $p-bg;
    padding: 16px 22px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__tabs {
    align-self: flex-start;
  }

  &__tab-icon {
    font-size: 14px !important;
    line-height: 1;
  }

  &__panel {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  // ── 섹션 ──
  &__section {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-lg;
    padding: 16px 18px;
  }

  &__section-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid $p-line-soft;
  }

  &__section-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__section-title {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $p-ink2;
  }

  &__section-extra {
    margin-left: auto;
  }

  // ── 업로드 그리드 ──
  &__upload-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-areas:
      "file action"
      "meta meta";
    gap: 12px;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
      grid-template-areas:
        "file"
        "action"
        "meta";
    }
  }

  &__upload-file { grid-area: file; }
  &__upload-action {
    grid-area: action;
    display: flex;
    align-items: flex-end;
  }
  &__upload-meta { grid-area: meta; }

  // ── 진행 ──
  &__progress {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__progress-bar {
    flex: 1;
    height: 6px;
    background: $p-line-soft;
    border-radius: 3px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    background: $p-ink;
    transition: width $p-dur $p-ease;

    &--warn { background: $p-point-deep; }
  }

  &__progress-text {
    font-size: 11px;
    color: $p-ink-mid;
    font-family: $p-font-mono;
    flex-shrink: 0;
  }

  // ── 로딩 ──
  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  // ── 이미지 그리드 ──
  &__images {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 14px;
  }

  &__image-card {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    transition: border-color $p-dur $p-ease, transform $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
      transform: translateY(-2px);
    }
  }

  &__image-thumb {
    position: relative;
    height: 140px;
    background: $p-panel-alt;
    overflow: hidden;

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform $p-dur $p-ease;
    }
  }

  &__image-overlay {
    position: absolute;
    inset: 0;
    background: rgba(26, 22, 18, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    opacity: 0;
    transition: opacity $p-dur $p-ease;

    .exf__image-thumb:hover & { opacity: 1; }
    .exf__image-thumb:hover > img { transform: scale(1.04); }
  }

  &__image-meta {
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__image-name {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__image-size {
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__image-tags {
    margin-top: 4px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  // ── 오디오 그리드 ──
  &__audios {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
  }

  &__audio-card {
    display: flex;
    gap: 12px;
    padding: 12px 14px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__audio-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: $p-warn-soft;
    color: $p-warn;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i { font-size: 22px; }
  }

  &__audio-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__audio-name {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__audio-size {
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__audio-script {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-mid;
    line-height: 1.4;
    margin-top: 2px;
  }

  &__audio-script-icon {
    font-size: 12px !important;
    color: $p-ink-soft;
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__audio-actions {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
}
</style>
